<template>
  <div class="resource-list">
    <div class="list-header">
      <h3>相关资源</h3>
      <el-button type="primary" size="small" round @click="handleAddResource">
        <el-icon><Plus /></el-icon> 添加资源
      </el-button>
    </div>

    <div v-if="loading" class="loading-container">
      <el-skeleton :rows="3" animated />
    </div>

    <div v-else-if="!resources.length" class="empty-container">
      <el-empty description="暂无相关资源" />
    </div>

    <div v-else class="resource-cards">
      <el-card v-for="resource in resources" :key="resource.id" class="resource-card" :body-style="{ padding: '16px' }">
        <div class="card-header">
          <div class="title-section">
            <h4>{{ resource.title }}</h4>
            <el-tag 
              :type="getResourceTypeTag(resource.type).type" 
              size="small"
              effect="light"
              round
            >
              {{ getResourceTypeTag(resource.type).label }}
            </el-tag>
            <el-tag 
              :type="resource.visibility === 'public' ? 'success' : 'warning'"
              size="small"
              effect="light"
              round
            >
              {{ resource.visibility === 'public' ? '公开' : '私密' }}
            </el-tag>
          </div>
          <div class="actions">
            <el-button 
              type="primary" 
              size="small"
              round
              text
              class="action-button edit-button"
              @click="handleEdit(resource)"
            >
              <el-icon><Edit /></el-icon>
              编辑
            </el-button>
            <el-button 
              type="danger" 
              size="small"
              round
              text
              class="action-button delete-button"
              @click="handleDelete(resource)"
            >
              <el-icon><Delete /></el-icon>
              删除
            </el-button>
          </div>
        </div>

        <div class="card-content">
          <div v-if="resource.content" class="content-preview">
            {{ resource.content.slice(0, 100) }}{{ resource.content.length > 100 ? '...' : '' }}
          </div>

          <div v-if="resource.url" class="resource-url">
            <el-link :href="resource.url" target="_blank" type="primary">
              <el-icon><Link /></el-icon> 查看原文
            </el-link>
          </div>

          <div v-if="resource.type === 'referral'" class="referral-info">
            <div v-if="resource.referralContact" class="info-item">
              <span class="label">推荐人：</span>
              <span>{{ resource.referralContact }}</span>
            </div>
            <div v-if="resource.referralCode" class="info-item">
              <span class="label">推荐码：</span>
              <span>{{ resource.referralCode }}</span>
            </div>
            <div v-if="resource.referralDeadline" class="info-item">
              <span class="label">截止日期：</span>
              <span>{{ formatDate(resource.referralDeadline) }}</span>
            </div>
          </div>

          <div class="meta-info">
            <span class="time">{{ formatDate(resource.createTime) }}</span>
            <div class="tags" v-if="resource.tags?.length">
              <el-tag
                v-for="tag in resource.tags"
                :key="tag.id"
                size="small"
                effect="plain"
                round
              >
                {{ tag.name }}
              </el-tag>
            </div>
          </div>
        </div>
      </el-card>
    </div>

    <div class="pagination-container" v-if="total > 0">
      <el-pagination
        v-model:current-page="currentPage"
        v-model:page-size="pageSize"
        :page-sizes="[10, 20, 50]"
        :total="total"
        layout="total, sizes, prev, pager, next"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>

    <!-- 添加/编辑资源对话框 -->
    <el-dialog
      v-model="dialogVisible"
      :title="editingResource ? '编辑资源' : '添加资源'"
      width="600px"
    >
      <el-form
        ref="formRef"
        :model="form"
        :rules="rules"
        label-width="100px"
      >
        <el-form-item label="资源类型" prop="type">
          <el-select v-model="form.type" placeholder="请选择资源类型">
            <el-option
              v-for="type in resourceTypes"
              :key="type.value"
              :label="type.label"
              :value="type.value"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="标题" prop="title">
          <el-input v-model="form.title" placeholder="请输入资源标题" />
        </el-form-item>

        <el-form-item label="URL" prop="url">
          <el-input v-model="form.url" placeholder="请输入资源链接" />
        </el-form-item>

        <el-form-item label="内容" prop="content">
          <el-input
            v-model="form.content"
            type="textarea"
            :rows="4"
            placeholder="请输入资源内容"
          />
        </el-form-item>

        <el-form-item label="可见性" prop="visibility">
          <el-radio-group v-model="form.visibility">
            <el-radio label="public">公开</el-radio>
            <el-radio label="private">私密</el-radio>
          </el-radio-group>
        </el-form-item>

        <template v-if="form.type === 'referral'">
          <el-form-item label="推荐人" prop="referralContact">
            <el-input v-model="form.referralContact" placeholder="请输入推荐人" />
          </el-form-item>

          <el-form-item label="推荐码" prop="referralCode">
            <el-input v-model="form.referralCode" placeholder="请输入推荐码" />
          </el-form-item>

          <el-form-item label="截止日期" prop="referralDeadline">
            <el-date-picker
              v-model="form.referralDeadline"
              type="date"
              placeholder="请选择截止日期"
            />
          </el-form-item>
        </template>

        <el-form-item label="标签" prop="tagIds">
          <tag-select v-model="form.tagIds" />
        </el-form-item>
      </el-form>

      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSubmit">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, watch } from 'vue';
import { ElMessage, ElMessageBox, FormInstance } from 'element-plus';
import { Plus, Link, Edit, Delete } from '@element-plus/icons-vue';
import type { Resource, AddResourceRequest, UpdateResourceRequest } from '@/types/resource';
import { ResourceType } from '@/types/resource';
import resourceApi from '@/services/resourceApi';
import { formatDate } from '@/utils/format';
import TagSelect from '@/components/tag/TagSelect.vue';
import { usePositionStore } from '@/store/positionStore';

const props = defineProps<{
  companyId?: number;
  positionId?: number;
}>();

const emit = defineEmits<{
  (e: 'refresh'): void;
}>();

// 状态变量
const loading = ref<boolean>(false);
const resources = ref<Resource[]>([]);
const currentPage = ref<number>(1);
const pageSize = ref<number>(10);
const total = ref<number>(0);
const dialogVisible = ref<boolean>(false);
const editingResource = ref<Resource | null>(null);
const formRef = ref<FormInstance>();

// 表单数据
const form = ref<AddResourceRequest>({
  companyId: props.companyId!,
  positionId: props.positionId,
  type: ResourceType.INTERVIEW_EXPERIENCE,
  title: '',
  url: '',
  content: '',
  visibility: 'private',
  referralContact: '',
  referralCode: '',
  referralDeadline: '',
  tagIds: []
});

// 表单校验规则
const rules = {
  type: [{ required: true, message: '请选择资源类型' }],
  title: [{ required: true, message: '请输入资源标题' }],
  visibility: [{ required: true, message: '请选择可见性' }]
};

// 资源类型选项
const resourceTypes = [
  { label: '面试经验', value: ResourceType.INTERVIEW_EXPERIENCE },
  { label: '内推信息', value: ResourceType.REFERRAL },
  { label: '公司信息', value: ResourceType.COMPANY_INFO },
  { label: '其他', value: ResourceType.OTHER }
];

// 获取资源类型标签
const getResourceTypeTag = (type: ResourceType) => {
  switch (type) {
    case ResourceType.INTERVIEW_EXPERIENCE:
      return { type: 'primary', label: '面试经验' };
    case ResourceType.REFERRAL:
      return { type: 'success', label: '内推信息' };
    case ResourceType.COMPANY_INFO:
      return { type: 'warning', label: '公司信息' };
    case ResourceType.OTHER:
      return { type: 'info', label: '其他' };
    default:
      return { type: 'info', label: '未知' };
  }
};

// 监听props变化
watch(() => props.companyId, (newVal) => {
  if (newVal) {
    currentPage.value = 1;
    loadResources();
  }
});

watch(() => props.positionId, (newVal) => {
  if (newVal) {
    currentPage.value = 1;
    loadResources();
  }
});

// 加载资源列表
const loadResources = async () => {
  if (!props.companyId && !props.positionId) {
    return;
  }

  loading.value = true;
  try {
    let response;
    if (props.positionId) {
      console.log('Loading resources for position:', props.positionId);
      response = await resourceApi.getResourcesByPosition(
        props.positionId,
        currentPage.value,
        pageSize.value
      );
    } else if (props.companyId) {
      console.log('Loading resources for company:', props.companyId);
      response = await resourceApi.getResourcesByCompany(
        props.companyId,
        currentPage.value,
        pageSize.value
      );
    }
    console.log('Received response:', response);
    if (response) {
      console.log('Setting resources:', response.content);
      console.log('Setting total:', response.totalElements);
      resources.value = response.content || [];
      total.value = response.totalElements || 0;
    }
  } catch (error) {
    console.error('加载资源列表失败:', error);
    ElMessage.error('加载资源列表失败');
    resources.value = [];
    total.value = 0;
  } finally {
    loading.value = false;
  }
};

// 处理添加资源
const handleAddResource = () => {
  editingResource.value = null;
  if (!props.companyId && !props.positionId) {
    ElMessage.warning('请先选择一个公司或职位');
    return;
  }
  
  form.value = {
    companyId: props.companyId || 0,
    positionId: props.positionId,
    type: ResourceType.INTERVIEW_EXPERIENCE,
    title: '',
    url: '',
    content: '',
    visibility: 'private',
    referralContact: '',
    referralCode: '',
    referralDeadline: '',
    tagIds: []
  };
  dialogVisible.value = true;
};

// 处理编辑资源
const handleEdit = (resource: Resource) => {
  editingResource.value = resource;
  form.value = {
    ...resource,
    tagIds: resource.tags?.map(tag => tag.id) || []
  };
  dialogVisible.value = true;
};

// 处理删除资源
const handleDelete = async (resource: Resource) => {
  try {
    await ElMessageBox.confirm('确定要删除该资源吗？', '提示', {
      type: 'warning'
    });
    await resourceApi.deleteResource(resource.id!);
    ElMessage.success('删除成功');
    loadResources();
  } catch (error) {
    if (error !== 'cancel') {
      console.error('删除资源失败:', error);
      ElMessage.error('删除资源失败');
    }
  }
};

// 处理表单提交
const handleSubmit = async () => {
  if (!formRef.value) return;
  
  await formRef.value.validate(async (valid) => {
    if (valid) {
      try {
        // 如果没有选择公司，但有选择职位，则先获取职位对应的公司ID
        if (!form.value.companyId && form.value.positionId) {
          const positionStore = usePositionStore();
          
          // 加载职位详情
          try {
            await positionStore.getPosition(form.value.positionId);
            if (positionStore.currentPosition?.company?.id) {
              form.value.companyId = positionStore.currentPosition.company.id;
            } else {
              ElMessage.error('无法确定资源关联的公司，请重试');
              return;
            }
          } catch (error) {
            console.error('获取职位信息失败:', error);
            ElMessage.error('获取职位信息失败，无法确定关联公司');
            return;
          }
        }
        
        if (editingResource.value) {
          await resourceApi.updateResource(editingResource.value.id!, {
            id: editingResource.value.id!,
            companyId: form.value.companyId,
            title: form.value.title,
            content: form.value.content,
            visibility: form.value.visibility,
            referralContact: form.value.referralContact,
            referralCode: form.value.referralCode,
            referralDeadline: form.value.referralDeadline,
            tagIds: form.value.tagIds
          });
          ElMessage.success('更新成功');
        } else {
          await resourceApi.addResource(form.value);
          ElMessage.success('添加成功');
        }
        dialogVisible.value = false;
        loadResources();
        emit('refresh');
      } catch (error) {
        console.error('保存资源失败:', error);
        ElMessage.error('保存失败');
      }
    }
  });
};

// 处理分页
const handleSizeChange = (size: number) => {
  pageSize.value = size;
  loadResources();
};

const handleCurrentChange = (page: number) => {
  currentPage.value = page;
  loadResources();
};

// 初始化
onMounted(() => {
  loadResources();
});
</script>

<style scoped>
.resource-list {
  margin-top: 20px;
}

.list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.resource-cards {
  display: grid;
  gap: 16px;
}

.resource-card {
  transition: all 0.3s ease;
  border-radius: 8px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 12px;

    .title-section {
      display: flex;
      align-items: center;
      gap: 8px;

      h4 {
        margin: 0;
        font-size: 16px;
        font-weight: 600;
      }
    }

    .actions {
      display: flex;
      gap: 8px;

      .action-button {
        padding: 6px 12px;
        border-radius: 20px;
        transition: all 0.3s ease;
        opacity: 0.8;

        &:hover {
          opacity: 1;
          transform: scale(1.05);
        }

        .el-icon {
          margin-right: 4px;
        }
      }

      .edit-button {
        color: white;
        background-color: var(--el-color-primary-light-5);
        
        &:hover {
          color: white;
          background: var(--el-color-primary);
        }
      }

      .delete-button {
        color: white;
        background-color: var(--el-color-danger-light-3);
        
        &:hover {
          color: white;
          background: var(--el-color-danger);
        }
      }
    }
  }

  .card-content {
    .content-preview {
      margin: 12px 0;
      color: #666;
      line-height: 1.5;
    }

    .resource-url {
      margin: 8px 0;
    }

    .referral-info {
      margin: 12px 0;
      padding: 12px;
      background-color: var(--el-color-info-light-9);
      border-radius: 8px;

      .info-item {
        margin: 4px 0;

        .label {
          color: #666;
          margin-right: 8px;
          font-weight: 500;
        }
      }
    }

    .meta-info {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-top: 12px;
      padding-top: 12px;
      border-top: 1px solid var(--el-border-color-lighter);

      .time {
        color: #999;
        font-size: 0.9em;
      }

      .tags {
        display: flex;
        gap: 8px;
      }
    }
  }
}

.pagination-container {
  margin-top: 20px;
  display: flex;
  justify-content: center;
}

.loading-container,
.empty-container {
  padding: 40px 0;
  text-align: center;
}
</style> 